const CAMP_STATES = ["open", "rallying", "supplied", "abandoned"] as const;
const PROJECTION_KEYS = ["schema", "generation", "revision", "frontId", "cellId", "state"] as const;
const GENERATION_KEYS = ["chroniclePublicId", "rulesVersion", "bindingRevision"] as const;
const PUBLIC_IDENTIFIER = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,95}$/;
const INVALID_PROJECTION = "invalid-heart-front1-camp-public-projection";

export type HeartFront1CampState = (typeof CAMP_STATES)[number];
export type HeartFront1CampLoadStatus = "loading" | "fresh" | "refreshing" | "stale" | "error";

export interface HeartFront1CampPublicProjection {
  readonly schema: "vaelor-front1-camp-public/v1";
  readonly generation: Readonly<{
    chroniclePublicId: string;
    rulesVersion: string;
    bindingRevision: number;
  }>;
  readonly revision: number;
  readonly frontId: "heart-front-1";
  readonly cellId: "heart:0:-9";
  readonly state: HeartFront1CampState;
}

export interface HeartFront1CampReadModel {
  readonly status: HeartFront1CampLoadStatus;
  readonly projection?: HeartFront1CampPublicProjection;
  readonly readAtMs: number | null;
  readonly error?: "État du camp indisponible";
  readonly announcement?: "Nouvelle génération du camp chargée";
}

export interface HeartFront1CampPresentation {
  readonly title: string;
  readonly detail: string;
  readonly freshness: string;
  readonly state: HeartFront1CampState;
}

export const HEART_FRONT1_CAMP_READ_INTERVAL_MS = 60_000;

export interface HeartFront1CampPostReceiptRefresh {
  readonly currentRead: () => Promise<void> | null;
  readonly isMounted: () => boolean;
  readonly read: () => Promise<void> | undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactPlainDataShape(
  value: unknown,
  expectedKeys: readonly string[],
): value is Record<string, unknown> {
  try {
    if (!isRecord(value) || Object.getPrototypeOf(value) !== Object.prototype) return false;
    const ownNames = Object.getOwnPropertyNames(value);
    if (ownNames.length !== expectedKeys.length || Object.getOwnPropertySymbols(value).length !== 0) {
      return false;
    }
    const expected = new Set(expectedKeys);
    return ownNames.every((name) => {
      if (!expected.has(name)) return false;
      const descriptor = Object.getOwnPropertyDescriptor(value, name);
      return descriptor !== undefined && "value" in descriptor && descriptor.enumerable === true;
    }) && expectedKeys.every((key) => ownNames.includes(key));
  } catch {
    return false;
  }
}

function isNonNegativeSafeInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 0;
}

function isPositiveSafeInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 1;
}

function isPublicIdentifier(value: unknown): value is string {
  return typeof value === "string" && PUBLIC_IDENTIFIER.test(value);
}

function isCampState(value: unknown): value is HeartFront1CampState {
  return typeof value === "string" && CAMP_STATES.includes(value as HeartFront1CampState);
}

function invalidProjection(): never {
  throw new TypeError(INVALID_PROJECTION);
}

export function parseHeartFront1CampPublicProjection(
  value: unknown,
): HeartFront1CampPublicProjection {
  if (!hasExactPlainDataShape(value, PROJECTION_KEYS) ||
      value.schema !== "vaelor-front1-camp-public/v1" ||
      value.frontId !== "heart-front-1" ||
      value.cellId !== "heart:0:-9" ||
      !isNonNegativeSafeInteger(value.revision) ||
      !isCampState(value.state)) {
    return invalidProjection();
  }
  const generation = value.generation;
  if (!hasExactPlainDataShape(generation, GENERATION_KEYS) ||
      !isPublicIdentifier(generation.chroniclePublicId) ||
      !isPublicIdentifier(generation.rulesVersion) ||
      !isPositiveSafeInteger(generation.bindingRevision)) {
    return invalidProjection();
  }
  return Object.freeze({
    schema: "vaelor-front1-camp-public/v1" as const,
    generation: Object.freeze({
      chroniclePublicId: generation.chroniclePublicId,
      rulesVersion: generation.rulesVersion,
      bindingRevision: generation.bindingRevision,
    }),
    revision: value.revision,
    frontId: "heart-front-1" as const,
    cellId: "heart:0:-9" as const,
    state: value.state,
  });
}

export function createHeartFront1CampReadModel(): HeartFront1CampReadModel {
  return { status: "loading", readAtMs: null };
}

export function beginHeartFront1CampRead(
  model: HeartFront1CampReadModel,
): HeartFront1CampReadModel {
  return {
    ...model,
    status: model.projection === undefined ? "loading" : "refreshing",
    error: model.status === "stale" ? model.error : undefined,
    announcement: undefined,
  };
}

export function settleHeartFront1CampSuccess(
  model: HeartFront1CampReadModel,
  projection: HeartFront1CampPublicProjection,
  readAtMs: number,
): HeartFront1CampReadModel {
  const previous = model.projection?.generation;
  const next = projection.generation;
  const generationChanged = previous !== undefined && (
    previous.chroniclePublicId !== next.chroniclePublicId ||
    previous.rulesVersion !== next.rulesVersion ||
    previous.bindingRevision !== next.bindingRevision
  );
  return {
    status: "fresh",
    projection,
    readAtMs,
    announcement: generationChanged ? "Nouvelle génération du camp chargée" : undefined,
  };
}

export function settleHeartFront1CampFailure(
  model: HeartFront1CampReadModel,
): HeartFront1CampReadModel {
  return {
    ...model,
    status: model.projection === undefined ? "error" : "stale",
    error: "État du camp indisponible",
    announcement: undefined,
  };
}

export async function rereadHeartFront1CampAfterReceipt({
  currentRead,
  isMounted,
  read,
}: HeartFront1CampPostReceiptRefresh): Promise<void> {
  if (!isMounted()) return;
  const priorRead = currentRead();
  if (priorRead !== null) {
    await priorRead.catch(() => undefined);
  }
  if (!isMounted()) return;
  await read();
}

function campFreshness(status: "fresh" | "stale", readAtMs: number | null): string {
  if (readAtMs === null) return "Lecture indisponible";
  const localTime = new Date(readAtMs).toLocaleString("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  });
  return status === "stale"
    ? `Périmée · dernière lecture ${localTime}`
    : `Fraîche · dernière lecture ${localTime}`;
}

const CAMP_PRESENTATIONS: Record<HeartFront1CampState, Readonly<{ title: string; detail: string }>> = {
  open: {
    title: "Camp ouvert",
    detail: "Le seuil extérieur attend une présence serveur confirmée.",
  },
  rallying: {
    title: "Ralliement en cours",
    detail: "Des colonnes convergent, mais la route A1 n’est pas encore maintenue.",
  },
  supplied: {
    title: "Camp ravitaillé",
    detail: "La présence autoritaire maintient actuellement la route A1.",
  },
  abandoned: {
    title: "Camp abandonné",
    detail: "Aucune présence autoritaire ne maintient actuellement la route A1.",
  },
};

export function presentHeartFront1Camp(
  projection: HeartFront1CampPublicProjection,
  readAtMs: number | null,
  status: "fresh" | "stale" = "fresh",
): HeartFront1CampPresentation {
  const copy = CAMP_PRESENTATIONS[projection.state];
  return {
    title: copy.title,
    detail: copy.detail,
    freshness: campFreshness(status, readAtMs),
    state: projection.state,
  };
}
